import * as React from 'react';
import {
    List,
    Datagrid,
    TextField,
    FunctionField,
    Confirm,
    useNotify,
} from 'react-admin';
import { Box, Typography } from '@mui/material';
import { IconButton } from '../icon-button/IconButton.tsx';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { CommissionCreateDialog } from '../dialogs/CommissionManagementDialog.tsx';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { COMMISSIONS } from '../../enums/Commissions.ts';
import { CommissionService } from '../../services/commission/commissionService.ts';
import { CommissionEditDialog } from '../dialogs/CommissionEditDialog.tsx';
import { EmptyBox } from '../empty-box/EmptyBox.tsx';
import * as Sentry from '@sentry/react';

//TODO: move styles to a separate file if possible

const CommissionTableActions = () => {
    const [open, setOpen] = React.useState(false);

    const { t } = useTranslation();

    return (
        <Box
            sx={{
                paddingTop: 'var(--space-72)',
                paddingBottom: 'var(--space-32)',
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
            }}
        >
            <Typography variant="h5" sx={{
                paddingLeft: 'var(--space-24)',
                fontWeight: 700,
                fontSize: 'var(--text-xxl)',
            }}>
                {t('resources.commissionManagement')}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <IconButton icon={<AddIcon />} label={t('commissionManagement.addNewAgency').toLocaleUpperCase()} onClick={() => setOpen(true)} />
            </Box>
            <CommissionCreateDialog open={open} onClose={() => setOpen(false)} />
        </Box>
    );
};

export const CommissionTable = () => {
    const notify = useNotify();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const [toDelete, setToDelete] = React.useState<number | null>(null);
    const commissionService = new CommissionService();
    const [open, setOpen] = React.useState(false);
    const [selectedId, setSelectedId] = React.useState<string | null>(null);

    type RoleKey = keyof typeof COMMISSIONS;
    type RoleLabel = (typeof COMMISSIONS)[RoleKey]['label'];

    return (
        <>
            <List
                actions={<CommissionTableActions />}
                perPage={25}
                empty={<EmptyBox
                    title={t('commissionManagement.emptyBox.title')}
                    description={t('commissionManagement.emptyBox.description')}
                    resource="commissionManagement"
                />}
                sx={{
                    '& .RaList-main': { px: 4 },
                    '& .RaList-content': {
                        border: '1px solid var(--color-secondary)',
                        borderRadius: 'var(--radius-6)',
                    },
                    '& .RaDatagrid-tbody tr:last-of-type td': {
                        borderBottom: 'none',
                    },
                    '& th.MuiTableCell-root': {
                        paddingLeft: 'var(--space-24)',
                        py: 'var(--space-16)',
                        fontSize: 'var(--text-xs)',
                    },
                    '& td.MuiTableCell-root': {
                        py: 'var(--space-24)',
                        paddingLeft: 'var(--space-24)',
                        fontSize: 'var(--text-xs)',
                    },
                    '& .MuiTablePagination-root': {
                        '& .MuiSelect-select': {
                            paddingRight: '32px !important',
                            paddingLeft: '12px !important',
                        },
                        '& .MuiSelect-icon': {
                            right: '8px',
                        }
                    }
                }}
            >
                <Datagrid
                    bulkActionButtons={false}
                    rowClick={false}
                    sx={{
                        '& .RaDatagrid-headerCell': {
                            fontWeight: 700,
                            letterSpacing: 0.2,
                        },
                    }}
                >
                    <TextField source="id" label={t('commissionManagement.columns.id')} sortable={false} />
                    <TextField source="agencyName" label={t('commissionManagement.columns.name')} sortable={false} />
                    <FunctionField
                        sortable={false}
                        label={t('commissionManagement.columns.percentOfOrder')}
                        render={(record) => {
                            const values = (record?.agencyCommissionList ?? [])
                                .filter((commission: { id: number; amount: number; commissionType: RoleLabel }) => commission?.commissionType === 'Percent of order')
                                .map((commission: { id: number; amount: number; commissionType: RoleLabel }) => commission?.amount);
                            return values.length ? values[0] + COMMISSIONS.PERCENT_OF_ORDER.unit : '-';
                        }}
                    />

                    <FunctionField
                        sortable={false}
                        label={t('commissionManagement.columns.kilometrage')}
                        render={(record) => {
                            const vals = (record?.agencyCommissionList ?? [])
                                .filter((commission: { id: number; amount: number; commissionType: RoleLabel }) => commission?.commissionType === 'Kilometrage')
                                .map((commission: { id: number; amount: number; commissionType: RoleLabel }) => commission?.amount);
                            return vals.length ? vals[0] + COMMISSIONS.KILOMETRAGE.unit : '-';
                        }}
                    />

                    <FunctionField
                        sortable={false}
                        label={t('commissionManagement.columns.basicFareCost')}
                        render={(record) => {
                            const vals = (record?.agencyCommissionList ?? [])
                                .filter((commission: { id: number; amount: number; commissionType: RoleLabel }) => commission?.commissionType === 'Basic fare cost')
                                .map((commission: { id: number; amount: number; commissionType: RoleLabel }) => commission?.amount);
                            return vals.length ? vals[0] + COMMISSIONS.BASIC_FARE_COST.unit : '-';
                        }}
                    />

                    <FunctionField
                        sortable={false}
                        label={t('commissionManagement.columns.monthlyFee')}
                        render={(record) => {
                            const vals = (record?.agencyCommissionList ?? [])
                                .filter((commission: { id: number; amount: number; commissionType: RoleLabel }) => commission?.commissionType === 'Monthly fee')
                                .map((commission: { id: number; amount: number; commissionType: RoleLabel }) => commission?.amount);

                            return vals.length ? vals[0] + COMMISSIONS.MONTHLY_FEE.unit : '-';
                        }}
                    />
                    <FunctionField
                        label=""
                        sortable={false}
                        render={record => (
                            <>
                                <IconButton icon={<DeliveryDiningIcon />} onClick={() =>
                                    navigate(`/commission/${record.id}/couriers`, {
                                        state: { agencyId: record.id, agencyName: record.agencyName },
                                    })
                                } />
                                <IconButton icon={<EditIcon />} onClick={() => { setSelectedId(record.id); setOpen(true); }} />
                                <IconButton icon={<DeleteOutlineIcon />} color="var(--color-text-primary)" onClick={() => {
                                    setConfirmOpen(true);
                                    setToDelete(record.id);
                                }}
                                />
                            </>
                        )}
                    />
                </Datagrid>
            </List>

            <Confirm
                isOpen={confirmOpen}
                title={t('commissionManagement.confirmDeletion.confirmDeletionTitle',)}
                content={t('commissionManagement.confirmDeletion.confirmDeletionBody')}
                confirm={t('common.delete')}
                cancel={t('common.cancel')}
                onConfirm={async () => {
                    try {
                        await commissionService.deleteAgency(toDelete!)
                        notify(t('common.saved'), { type: 'success' });
                        setConfirmOpen(false);
                        setToDelete(null);
                    }
                    catch (error) {
                        notify(error.response.data.error, { type: 'error' });
                        console.error(error.response.data.error);
                        Sentry.captureException(error);
                        setConfirmOpen(false);
                        setToDelete(null);
                    }
                }}
                onClose={() => {
                    setConfirmOpen(false);
                    setToDelete(null);
                }}
            />

            {open && (
                <CommissionEditDialog
                    id={selectedId!}
                    open={open}
                    onClose={() => setOpen(false)}
                />
            )}

        </>
    )
};
